"use client";

import { useEffect, useRef, useState } from "react";
import { Box, Spinner, Heading, Tooltip, Flex } from "@chakra-ui/react";
import { InfoIcon } from "@chakra-ui/icons";
import axios from "axios";
import echarts from "./lib";

// Teal bars + red forecast line — the header info text promises exactly
// these colours, so they stay; only the treatment is upgraded.
const RED = "#BF3F38";

const fmtFull = (v) => Number(v).toLocaleString("en-US");

const Chart9 = ({ supplier, retailer }) => {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [noData, setNoData] = useState(false);

  useEffect(() => {
    let disposed = false;

    const build = async () => {
      if (!containerRef.current) return;
      setLoading(true);
      try {
        const { data } = await axios.post(
          `${process.env.REACT_APP_API_URL}/rep_sales/chart9`,
          { supplier, retailer, userid: localStorage.getItem("userid") }
        );

        if (disposed) return;

        // Identical transforms to the AmCharts version — the DATA must not
        // change: all-zero check, unique periods, series split by name.
        const values = data?.data || [];
        const allZero = values.length > 0 && values.every((v) => v.value === 0);
        if (allZero) {
          setNoData(true);
          return;
        }
        setNoData(false);

        const weeks = [...new Set(values.map((d) => d.period))];
        const actualByPeriod = new Map(
          values.filter((d) => d.series === "Actual").map((d) => [d.period, d.value])
        );
        const forecastByPeriod = new Map(
          values.filter((d) => d.series === "Forecast").map((d) => [d.period, d.value])
        );
        const actual = weeks.map((w) =>
          actualByPeriod.has(w) ? actualByPeriod.get(w) : null
        );
        const forecast = weeks.map((w) =>
          forecastByPeriod.has(w) ? forecastByPeriod.get(w) : null
        );

        if (!containerRef.current) return;
        const chart = echarts.init(containerRef.current);
        chartRef.current = chart;
        chart.setOption({
          grid: { left: 8, right: 14, top: 34, bottom: 4, containLabel: true },
          legend: {
            top: 0,
            left: 0,
            icon: "roundRect",
            itemWidth: 10,
            itemHeight: 10,
            itemGap: 14,
            textStyle: { color: "#4A5D6B", fontSize: 11.5 },
          },
          xAxis: {
            type: "category",
            data: weeks,
            axisLabel: { color: "#64798A", fontSize: 11, rotate: 20 },
            axisLine: { lineStyle: { color: "#E2E9EE" } },
            axisTick: { show: false },
          },
          yAxis: {
            type: "value",
            axisLabel: {
              color: "#64798A",
              fontSize: 11,
              formatter: (v) =>
                v >= 1000000 ? `R${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `R${(v / 1000).toFixed(0)}K` : `R${v}`,
            },
            splitLine: { lineStyle: { color: "rgba(229,231,235,0.5)", type: [3, 3] } },
          },
          tooltip: {
            trigger: "axis",
            axisPointer: { type: "shadow", shadowStyle: { color: "rgba(20,163,178,0.06)" } },
            confine: true,
            backgroundColor: "#ffffff",
            borderColor: "#E2E9EE",
            borderWidth: 1,
            padding: [8, 12],
            textStyle: { color: "#1D2F3A", fontSize: 12 },
            extraCssText: "box-shadow: 0 6px 18px rgba(29,47,58,0.14); border-radius: 8px;",
            formatter: (params) => {
              const period = params[0]?.name || "";
              let a = null;
              let f = null;
              params.forEach((p) => {
                if (p.seriesName === "Actual") a = p.value;
                if (p.seriesName === "Forecast") f = p.value;
              });
              let out = `${period}`;
              if (a != null) out += `<br/>Actual: <b>R${fmtFull(a)}</b>`;
              if (f != null) out += `<br/>Forecast: <b>R${fmtFull(f)}</b>`;
              if (a != null && f != null && f !== 0) {
                const diff = ((a - f) / f) * 100;
                const beat = diff >= 0;
                out += `<br/><span style="color:${beat ? "#12775F" : "#BF3F38"}">${
                  beat ? "▲ beat" : "▼ missed"
                } forecast by ${Math.abs(diff).toFixed(1)}%</span>`;
              }
              return out;
            },
          },
          series: [
            {
              name: "Actual",
              type: "bar",
              data: actual,
              barWidth: "58%",
              itemStyle: {
                borderRadius: [5, 5, 0, 0],
                color: {
                  type: "linear",
                  x: 0,
                  y: 0,
                  x2: 0,
                  y2: 1,
                  colorStops: [
                    { offset: 0, color: "rgba(20,163,178,0.95)" },
                    { offset: 1, color: "rgba(20,163,178,0.3)" },
                  ],
                },
              },
              emphasis: {
                itemStyle: { shadowBlur: 10, shadowColor: "rgba(29,47,58,0.22)" },
              },
              animationDuration: 800,
              animationDelay: (idx) => idx * 40,
              animationEasing: "cubicOut",
            },
            {
              name: "Forecast",
              type: "line",
              data: forecast,
              lineStyle: { color: RED, width: 2.5 },
              itemStyle: { color: "#ffffff", borderColor: RED, borderWidth: 2 },
              symbol: "circle",
              symbolSize: 8,
              z: 3,
              emphasis: { scale: 1.6 },
              animationDuration: 900,
            },
          ],
        });
      } catch (err) {
        console.error("Chart9 fetch error", err);
      } finally {
        if (!disposed) setLoading(false);
      }
    };

    build();

    const ro = new ResizeObserver(() => {
      if (chartRef.current) chartRef.current.resize();
    });
    if (containerRef.current) ro.observe(containerRef.current);

    return () => {
      disposed = true;
      ro.disconnect();
      if (chartRef.current) {
        chartRef.current.dispose();
        chartRef.current = null;
      }
    };
  }, [supplier, retailer]);

  return (
    <Box w="100%" h="100%" position="relative" display="flex" flexDirection="column">
      <Flex justify="space-between" align="center" mb={2}>
        <Heading size="xs" color="gray.600" fontWeight="600">Forecast vs Actual</Heading>
        <Tooltip label="Teal = weekly sales, Red line = seasonally adjusted forecast. Bars above line beat forecast; bars below miss it." hasArrow placement="left" maxW="240px" whiteSpace="normal">
          <InfoIcon w={3.5} h={3.5} cursor="pointer" color="gray.400" />
        </Tooltip>
      </Flex>
      <Box flex="1" position="relative">
        {noData && (
          <Flex align="center" justify="center" position="absolute" top="0" left="0" width="100%" height="100%" zIndex="1">
            <Heading size="sm" color="gray.600">
              No data available
            </Heading>
          </Flex>
        )}
        <div ref={containerRef} style={{ width: "100%", height: "100%", overflow: "hidden" }} />
        {loading && <Spinner position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)" />}
      </Box>
    </Box>
  );
};

export default Chart9;
